import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Provider from '../model/Provider';
import { UttuQuery } from '../api';
import { getUserContextQuery } from '../api/uttu/queries';

// Local storage keys
export const ACTIVE_PROVIDER = 'ACTIVE_PROVIDER';
export const ACTIVE_MAP_BASELAYER = 'ACTIVE_MAP_BASELAYER';

export interface UserContext {
  preferredName: string;
  isAdmin: boolean;
  providers: Provider[];
  activeProviderCode?: string | null;
  loaded: boolean;
  activeMapBaseLayer?: string | null;
}

const initialState: UserContext = {
  isAdmin: false,
  preferredName: '',
  providers: [],
  activeProviderCode: window.localStorage.getItem(ACTIVE_PROVIDER),
  loaded: false,
  activeMapBaseLayer: window.localStorage.getItem(ACTIVE_MAP_BASELAYER),
};

export const userContextSlice = createSlice({
  name: 'userContext',
  initialState,
  reducers: {
    setActiveProviderCode: (state, action) => {
      return {
        ...state,
        activeProviderCode: action.payload,
      };
    },
    /**
     * Updates the active base map layer in the Redux state.
     *
     * @param state - The current Redux state.
     * @param action - Contains the new base layer name as `payload`.
     * @returns A new state object with the updated `activeMapBaseLayer` value.
     */
    setActiveMapBaseLayer: (state, action) => {
      return {
        ...state,
        activeMapBaseLayer: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserContext.fulfilled, (state, action) => {
      state.isAdmin = action.payload.isAdmin;
      state.preferredName = action.payload.preferredName;
      state.providers = action.payload.providers;
      state.loaded = true;
    });
  },
});

export default userContextSlice.reducer;

export const { setActiveProviderCode, setActiveMapBaseLayer } =
  userContextSlice.actions;

export interface FetchUserContextArgs {
  uttuApiUrl?: string;
  getAccessToken: () => Promise<string>;
}

export const fetchUserContext = createAsyncThunk<
  UserContext,
  FetchUserContextArgs
>('fetchUserContext', async (args) => {
  const response = await UttuQuery(
    args.uttuApiUrl,
    'providers',
    getUserContextQuery,
    {},
    await args.getAccessToken(),
  );

  return response.userContext;
});
